import { useCallback, useEffect, useRef, useState } from "react";
import {
  getFetchJobStatus,
  getInventoryAlerts,
  getSkuDetail,
  submitFetchJob,
} from "../../../../../../../Service/DashboardServices/FBAInventoryServices/inventoryAlertsService";

const POLL_MS = 15000;

/**
 * Data + refresh logic for the Inventory Alerts page.
 *  reload()            -> rebuilds the screens from data already stored (fast)
 *  startFetchJob(force)-> asks Amazon for new data (the scheduled job), polls it, reloads when done
 */
export default function useInventoryAlerts(profileId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState(null); // overrides for this view
  const [job, setJob] = useState(null); // latest job status from the backend
  const [jobError, setJobError] = useState(null);
  const pollTimer = useRef(null);
  const alive = useRef(true);

  const reload = useCallback(
    async (overrides) => {
      if (!profileId) return;
      setLoading(true);
      setError(null);
      try {
        const next = await getInventoryAlerts({
          profileId,
          settings: overrides === undefined ? settings : overrides,
        });
        console.log("Inventory alerts response (JSON):\n" + JSON.stringify(next, null, 2));
        if (alive.current) setData(next);
      } catch (e) {
        if (alive.current) setError(e.message);
      } finally {
        if (alive.current) setLoading(false);
      }
    },
    [profileId, settings]
  );

  const applySettings = useCallback(
    (next) => {
      setSettings(next);
      return reload(next);
    },
    [reload]
  );

  const loadSku = useCallback((sku) => getSkuDetail({ profileId, sku, settings }), [profileId, settings]);

  const stopPolling = () => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
    pollTimer.current = null;
  };

  const poll = useCallback(
    async (jobId) => {
      stopPolling();
      try {
        const status = await getFetchJobStatus({ profileId, jobId });
        if (!alive.current) return;
        setJob(status);
        setJobError(null);
        if (status.job && !status.done) {
          pollTimer.current = setTimeout(() => poll(jobId), POLL_MS);
        } else if (status.job && status.done) {
          reload(); // new data is in: rebuild the screens
        }
      } catch (e) {
        if (alive.current) setJobError(e.message);
      }
    },
    [profileId, reload]
  );

  const startFetchJob = useCallback(
    async (forceReports = false) => {
      setJobError(null);
      try {
        const res = await submitFetchJob({ profileId, forceReports });
        if (!alive.current) return;
        setJob({ job: res.job, stage: "queued", done: false, message: res.message, created: res.created });
        poll(res.job.jobId);
      } catch (e) {
        if (alive.current) setJobError(e.message);
      }
    },
    [profileId, poll]
  );

  useEffect(() => {
    alive.current = true;
    setData(null);
    setJob(null);
    reload(null);
    poll(); // picks up a job that is already running for this store
    return () => {
      alive.current = false;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  const jobRunning = Boolean(job && job.job && !job.done);
  return { data, loading, error, reload, settings, applySettings, loadSku, job, jobRunning, jobError, startFetchJob };
}
